import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT_EMAIL } from "@/lib/site";
import { waLink } from "@/components/WhatsAppButton";
import { useLang } from "@/i18n";
import { trackEvent } from "@/lib/analytics";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Newsletter capture — posts to /api/newsletter (idempotent, welcome email). */
const NewsletterForm = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      setEmail("");
      trackEvent("newsletter_signup");
      toast.success(t("footer.newsSuccess"));
    } catch {
      toast.error(t("footer.newsError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="newsletter-form" onSubmit={subscribe} className="flex w-full max-w-md gap-2">
      <input
        type="email"
        required
        data-testid="newsletter-email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("footer.newsPlaceholder")}
        className="flex-1 min-w-0 border border-[#1d3a1f]/15 bg-white rounded-full px-5 py-3 text-sm text-[#1d3a1f] placeholder:text-[#a89a86] focus:outline-none focus:border-[#d3a53a] transition-colors duration-200"
      />
      <button
        type="submit"
        data-testid="newsletter-submit"
        disabled={busy}
        className="inline-flex items-center gap-2 bg-[#d3a53a] text-[#173a1e] text-sm font-bold px-6 py-3 rounded-full hover:bg-[#b98a26] transition-colors duration-200 disabled:opacity-60 shrink-0"
      >
        {busy ? "…" : t("footer.newsButton")} <Send size={14} />
      </button>
    </form>
  );
};

const Footer = () => {
  const { t } = useLang();

  const explore = [
    { label: t("footer.ourStory"), to: "/about" },
    { label: t("nav.fruit"), to: "/fruit-trading" },
    { label: t("nav.rootstock"), to: "/rootstock" },
    { label: t("nav.mango"), to: "/mango-export" },
    { label: t("nav.sourcing"), to: "/sourcing" },
    { label: t("nav.partners"), to: "/partners" },
    { label: t("nav.quality"), to: "/quality" },
    { label: t("nav.insights"), to: "/insights" },
    { label: t("nav.contact"), to: "/contact" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-[#fdfbf5] pt-20 pb-10">
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Newsletter band */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-b border-[#1d3a1f]/8 pb-12 mb-16">
          <div>
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{t("footer.newsTitle")}</h2>
            <p className="mt-2 text-[#5f6b57]">{t("footer.newsDesc")}</p>
          </div>
          <NewsletterForm />
        </div>

        <div className="flex flex-col lg:flex-row justify-between gap-12 border-b border-[#1d3a1f]/8 pb-16">
          <div className="max-w-md">
            <img src="/farmsons-logo-dark.png" alt="Farmsons logo" className="h-12 w-auto" />
            <p className="mt-5 text-[#5f6b57] leading-relaxed">{t("footer.blurb")}</p>
          </div>
          <div className="flex gap-16">
            <nav aria-label="Footer">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#5f6b57]">{t("footer.explore")}</span>
              <div className="mt-5 grid grid-cols-2 gap-x-10 gap-y-3">
                {explore.map((n) => (
                  <Link key={n.to} to={n.to} className="text-[#1d3a1f]/70 hover:text-[#d3a53a] transition-colors duration-200">{n.label}</Link>
                ))}
              </div>
            </nav>
            <div>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#5f6b57]">{t("footer.office")}</span>
              <address className="mt-5 flex flex-col gap-3 text-[#1d3a1f]/70 not-italic">
                <span>{t("footer.city")}</span>
                <span>{t("footer.country")}</span>
                <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-[#d3a53a] transition-colors break-all">{CONTACT_EMAIL}</a>
                {waLink && <a href={waLink} target="_blank" rel="noopener noreferrer" className="hover:text-[#d3a53a] transition-colors">WhatsApp</a>}
              </address>
            </div>
          </div>
        </div>
        <div className="pt-8 flex flex-col md:flex-row justify-between gap-4 text-sm text-[#5f6b57]">
          <span>© {new Date().getFullYear()} Farmsons. {t("footer.rights")}</span>
          <span>{t("footer.tagline")}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
